<template>
    <div 
        v-if="uid !== null"
        class="fileUpload dark"
        v-show="!hide"
    >
        <input type="file" ref="fileUploadRef" multiple="multiple" />        
    </div>
</template>

<script>
import axios from 'axios'
const fb = require('@/firebaseConfig.js')
import * as FilePond from 'filepond'
import FilePondPluginImageCrop from 'filepond-plugin-image-crop'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import FilePondPluginImageResize from 'filepond-plugin-image-resize'
import FilePondPluginImageTransform from 'filepond-plugin-image-transform'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'

export default {
    name: 'FileUpload',

    data(){
        return{
            // Core
            baseURL: 'https://storage.bunnycdn.com/onemgl/',
            accessKey: '2952c11d-f306-4a32-96d30e4408f5-eec7-4c30',
            client: null,
            imagePath: 'https://cdn.onemgl.com/',
            filesPath: 'files/',
            pond: null,
            
            // UI Stuff
            allFiles: [],
            loading: true,
        }
    },

    props: {
        path: String,
        uid: String,
        roomId: {
            value: String,
            default: null
        },
        hide: Boolean
    },

    mounted(){
        this.init()
    },
    
    afterDestroy(){
        this.pond.destroy(this.$refs.fileUploadRef)
    },

    methods: {
        /**
         * Storage Client
         */
        
        init(){
            this.setPath()
            this.createClient()
            this.initFilePond()
        },

        setPath(){
            let filesPath = ''

            // User Files Path
            if(typeof this.uid !== 'undefined'){
                filesPath += `${this.uid}/`
            }
            if(typeof this.path !== 'undefined'){
                filesPath += `${this.path}`
            }

            // Chat Files Path
            if(this.roomId !== null){
                filesPath = `${this.roomId}/${this.path}`
            }

            this.filesPath = filesPath
        },

        getPath(){
            return this.filesPath
        },

        createClient(){
            // BunnyCDN Storage docs:
            // https://github.com/69/bunnycdn-storage-js/blob/master/src/main.ts
            // https://www.npmjs.com/package/bunnycdn-storage/v/1.0.0

            this.client = axios.create({
                baseURL: `${this.baseURL}${this.filesPath}`,
                headers: {
                    AccessKey: this.accessKey
                },
                maxContentLength: Infinity
            })
            this.loading = false
        },

        /**
         * FilePond
         */

        initFilePond(){
            // FilePond Docs
            // https://pqina.nl/filepond/docs/
            let self = this
            
            // Register Plugins
            FilePond.registerPlugin(
                FilePondPluginImageCrop,
                FilePondPluginImagePreview,
                FilePondPluginImageResize,
                FilePondPluginImageTransform,
                // FilePondPluginFileValidateType
            )
            let allProcessedFiles = []

            this.pond = FilePond.create(this.$refs.fileUploadRef, {
                maxFiles: 10,
                credits: false,
                // acceptedFileTypes: [
                //     'image/*',
                //     'video/mp4',
                //     'video/mov'
                // ],
                // fileValidateTypeDetectType: (source, type) => new Promise((resolve, reject) => {
                //     resolve(type)
                // }),
                imageCropAspectRatio: 1,
                imageResizeTargetWidth: 512,
                imageResizeMode: 'contain',
                imageTransformVariantsIncludeOriginal: true,
                imageTransformVariants: {
                    small: transforms => {
                        transforms.resize.size.width = 256
                        return transforms
                    },
                    tiny: transforms => {
                        transforms.resize.size.width = 64
                        return transforms
                    }
                },
                oninit(){
                    allProcessedFiles = []
                },
                server: {
                    process: (fieldName, outputFiles, metadata, load, error, progress, abort, transfer, options) => {
                        
                        let batchProcess = []
                        let fileObj = {}
                        let timestamp = new Date().getTime()


                        if(Object.keys(outputFiles).length > 1){
                            outputFiles.forEach((output) => {
                                batchProcess.push(this.processFile(output, timestamp))
                            })
                        }else{
                            batchProcess.push(this.processFile(outputFiles, timestamp))
                        }
                        
                        Promise.all(batchProcess).then(values => {

                            // Merge Image Thumbnails into one object
                            if(values[0].type === 'image'){
                                
                                fileObj.type = values[0].type

                                for(let i = 0; i < values.length; i++){
                                    fileObj[values[i].thumbType] = values[i]

                                    if(values[i].thumbType === 'medium'){
                                        fileObj.id = values[i].raw
                                    }
                                }

                            // Videos and Other Files
                            }else{
                                fileObj = values[0]
                                fileObj.type = values[0].type
                                fileObj.id = values[0].raw
                            }
                            
                            // uploadedFiles.push(fileObj)
                            allProcessedFiles.push(fileObj)

                            load(fileObj)
                        })

                        return {
                            abort: () => {
                                abort()
                            }
                        }
                    },
                    revert: null
                },
                onprocessfiles(){
                    self.saveFilesToDb(allProcessedFiles)
                }
            })
        },

        processFile(output, timestamp){
            return new Promise(async(resolve, reject) => {
                
                // console.log('Process file: ', output.type)

                /**
                 * Detect File Type
                 */

                let fileType = ''

                if(typeof output.type === 'undefined'){
                    fileType = 'image'
                }else{
                    if(output.type.match('video')){
                        fileType = 'video'
                    }else{
                        fileType = 'other'
                    }
                }

                /**
                 * Images
                 */

                if(fileType === 'image'){

                    // Create tmp image
                    const img = new Image()
                    let imageUrl = URL.createObjectURL(output.file)
                    img.src = imageUrl

                    // Get File Metadata
                    let fileMetaData = this.getFileMetadata(output.file, timestamp)
                    let thumbType = ''

                    // Check for thumbnail
                    if(output.name === 'small' || output.name === 'tiny'){
                        thumbType = output.name
                    }else if(output.name === 'original_'){
                        thumbType = 'original'
                    }else{
                        thumbType = 'medium'
                    }
                    fileMetaData.name = `${fileMetaData.raw}_${thumbType}.${fileMetaData.extension}`

                    // Upload File
                    let uploadedFileData = await this.uploadFile(output.file, fileMetaData)

                    uploadedFileData.type = fileMetaData.type
                    uploadedFileData.thumbType = thumbType

                    // Remove tmp image
                    URL.revokeObjectURL(output.file)

                    // Return file object
                    resolve(uploadedFileData)
                
                }else if(fileType === 'video'){

                        // Get File Metadata
                        let fileMetaData = this.getFileMetadata(output, timestamp)
                        
                        // Upload File
                        let uploadedFileData = await this.uploadFile(output, fileMetaData)

                        // Try Generate Thumbnail
                        let videoPoster = null

                        try {
                            videoPoster = await this.getVideoPoster(output, timestamp, 1.5)
                            
                            console.log('Successfully received video poster: ', videoPoster)
                        } catch (err) {
                            console.log('Error generating video poster: ', err)
                        }

                        if(videoPoster !== null){
                            console.log('Starting to upload video poster...')

                            // Create tmp image
                            const img = new Image()
                            let imageUrl = URL.createObjectURL(videoPoster)
                            img.src = imageUrl

                            console.log('New Image created: ', imageUrl)

                            let thumbFileMetaData = this.getFileMetadata(videoPoster, timestamp)
                            let thumbType = 'poster'

                            thumbFileMetaData.name = `${thumbFileMetaData.raw}_${thumbType}.${thumbFileMetaData.extension}`
                            
                            // Upload Video Poster
                            let uploadedThumbFileData = await this.uploadFile(videoPoster, thumbFileMetaData)

                            uploadedThumbFileData.type = thumbFileMetaData.type
                            uploadedThumbFileData.thumbType = thumbType

                            // Attach poster info to file obj
                            uploadedFileData.poster = uploadedThumbFileData
                        }

                        // Return File Object
                        resolve(uploadedFileData)

                    /**
                     * Other Files
                     */

                }else if(fileType === 'other'){
                    // Get File Metadata
                    let fileMetaData = this.getFileMetadata(output, timestamp)

                    // Upload File
                    let uploadedFileData = await this.uploadFile(output, fileMetaData)

                    // Return File Object
                    resolve(uploadedFileData)
                }
                
            })
        },

        addFilesToPond(files){
            console.log('Add Files: ', files)

            // this.pond.addFiles(files)

            for(let i = 0; i < files.length; i++){
                this.pond.addFile(files[i])
            }

            console.log('All Files added successfully to pond!')
        },

        /**
         * Save to Firestore
         * User Files area.
         */

        async saveFilesToDb(files){

            let filesRef = fb.usersCollection.doc(this.uid).collection('files')

            if(this.roomId !== null){
                filesRef = fb.db.collection('chats').doc(this.roomId).collection('files')
            }

            let fileUploadedFiles = []
            
            for(let i = 0; i < files.length; i++){
                let fileObj = files[i]
                
                fileObj.userUid = this.uid

                let uploadedFile = await this.saveFile(filesRef, fileObj)

                fileUploadedFiles.push(uploadedFile)

                // console.log('Remove file from pond: ', fileObj)
                this.pond.removeFile(fileObj)
            }

            console.log('All saved files with uid: ', fileUploadedFiles)
            
            this.$emit('upload-success', fileUploadedFiles)
        },
        async saveFile(filesRef, fileObj){

            let fileUid = filesRef.doc().id
            let fileRef = filesRef.doc(fileUid)
            let newFileObj = {}

            newFileObj = fileObj
            newFileObj.uid = fileUid
            newFileObj.timestamp = fb.firebase.firestore.FieldValue.serverTimestamp()

            console.log('Save file with new uid: ', newFileObj, fileUid)

            return await fileRef.set(newFileObj).then(doc => {
                return newFileObj
            }).catch(err => {
                console.log('Error saving file to db: ', err)
                return fileObj
            })
        },
        async removeFile(fileUid){
            let userId = this.uid

            if(typeof userId === 'undefined'){
                userId = this.$store.state.currentUser.uid
            }

            console.log('Remove file in user: ', userId)

            let fileRef = fb.usersCollection.doc(userId).collection('files').doc(fileUid)

            if(this.roomId !== null){
                fileRef = fb.db.collection('chats').doc(this.roomId).collection('files').doc(fileUid)
            }

            return await fileRef.delete().then(() => {
                console.log('Successfully removed file with uid: ', fileUid)
                return true
            }).catch(err => {
                console.log('Error removing file from database: ', fileUid, err)
                return null
            })
        },

        /**
         * Actions
         */

        async uploadFile(file, meta){
            
            return await this.client({
                method: 'PUT',
                url: meta.name,
                data: file
            }).then(() => {
                console.log('Files :: Uploaded file: ', meta.name, meta)
                
                let fileObj = meta
                
                fileObj.path = `${meta.name}`
                fileObj.url = `${this.imagePath}${this.filesPath}${meta.name}`

                return fileObj

            }).catch(err => {
                console.log('Files :: Error uploading file: ', meta.name, meta)
                return null
            })
        },

        async deleteFiles(files){

            console.log('Delete files with uid: ', files)

            if(typeof files.uid === 'undefined'){
                console.log('Files uid is empty!')
                return
            }
            
            let batchProcess = []

            if(files.type === 'image'){
                batchProcess.push(await this.deleteFile(files.id, files.original))
                batchProcess.push(await this.deleteFile(files.id, files.medium))
                batchProcess.push(await this.deleteFile(files.id, files.small))
                batchProcess.push(await this.deleteFile(files.id, files.tiny))
            }else if(files.type === 'video'){
                batchProcess.push(await this.deleteFile(files.id, files))

                if(files.poster){
                    batchProcess.push(await this.deleteFile(files.id, files.poster))
                }
            }else if(files.type === 'other'){
                batchProcess.push(await this.deleteFile(files.id, files))
            }

            batchProcess.push(await this.removeFile(files.uid))

            Promise.all(batchProcess).then(values => {
                this.$emit('delete-success')
            })
        },

        async deleteFile(fileId, file){
            
            return await this.client({
                method: 'DELETE',
                url: file.path
            }).then(() => {
                return true
            }).catch(err => {
                console.log('Error deleting the file: ', fileId)
                return null
            })
        },

        async listFiles(){

            return await this.client({
                method: 'GET',
                url: `/`
            }).then(res => {
                // console.log('Files :: All files in storage: ', res)

                if(res.status === 200){
                    return res.data
                }else{
                    return []
                }
            }).catch(err => {
                console.log('Error listing all files in path: ', this.filesPath)
                return []
            })
        },

        // Returns a file metadata
        getFileMetadata(file, timestamp){
            // Extension
            let fileExt = (file.name).split('').reverse().join('')
            fileExt = fileExt.split('.')[0]
            fileExt = fileExt.split('').reverse().join('')
            
            // New Name
            let fileName = (file.name).split('.'+ fileExt)[0]
            let fileDateTime = timestamp
            let fileNameRaw = `${fileName}-${fileDateTime}`
            fileName = `${fileName}-${fileDateTime}.${fileExt}`
            
            // Size
            let fileSizeKb = file.size / 1024
            let fileSizeMb = file.size / 1024 / 1024

            // File Type
            let type = 'other'

            if(file.type.match('video')){
                type = 'video'
            }else if(file.type.match('image')){
                type = 'image'
            }

            let fileMeta = {
                name: fileName,
                raw: fileNameRaw,
                type: type,
                fileType: file.type,
                extension: fileExt,
                size: {
                    kb: fileSizeKb,
                    mb: fileSizeMb
                },
                timestamp: timestamp
            }

            return fileMeta
        },

        // Returns a video poster image
        getVideoPoster(file, timestamp, seekTo){
            // console.log('Getting video poster from file: ', file)

            return new Promise((resolve, reject) => {
                const videoPlayer = document.createElement('video')
                
                videoPlayer.setAttribute('src', URL.createObjectURL(file))
                videoPlayer.load()
                videoPlayer.addEventListener('error', (ex) => {
                    reject('Error getting poster from video', ex)
                })
                videoPlayer.addEventListener('loadedmetadata', () => {
                    if(videoPlayer.duration < seekTo){
                        reject('Video is too short')
                        return
                    }
                })
                setTimeout(() => {
                    videoPlayer.currentTime = seekTo
                }, 200)
                videoPlayer.addEventListener('seeked', () => {
                    // console.log('Video is now paused at %ss', seekTo)
                    
                    const canvas = document.createElement('canvas')
                    canvas.width = videoPlayer.videoWidth
                    canvas.height = videoPlayer.videoHeight
                    const ctx = canvas.getContext('2d')
                    ctx.drawImage(videoPlayer, 0, 0, canvas.width, canvas.height)
                    ctx.canvas.toBlob(blob => {
                        let file = new File([blob], 'video_poster.jpg', {type: "image/jpeg"})
                        resolve(file)
                    }, "image/jpeg")
                })
            })
        },
        
        /**
         * Helpers
         */

        triggerUploadBtn(){
            if(this.loading) return
            this.pond.browse()
        },

        /**
         * Deprecated
         */

        inputFilesHandler(e){
            let droppedFiles = e.target.files
            if(!droppedFiles) return
            this.prepareFilesForUpload(droppedFiles)
        },

        async prepareFilesForUpload(files){

            this.loading = true
        
            // Loop through each files
            console.log(`Files :: Ready to be uploaded (${files.length}): `, files)

            let uploadedFiles = []

            for(let i = 0; i < files.length; i++){
                
                let file = files[i]
                let metadata = this.getFileMetadata(file)
                
                // Upload original file
                let uploadedFileData = await this.uploadFile(file, metadata)

                if(uploadedFileData !== null){
                    uploadedFiles.push(uploadedFileData)
                }

                // Generate thumbnail and upload file
                let imageThumbnailData = await this.getImageThumbnail(uploadedFileData, metadata)
                let uploadedThumbFileData = await this.uploadFile(imageThumbnailData.thumb, imageThumbnailData.meta)

                if(uploadedThumbFileData !== null){
                    uploadedFiles.push(uploadedThumbFileData)
                }
            }

            console.log('Files :: All files have been uploaded successfully: ', uploadedFiles)

            this.loading = false

            this.$emit('upload-success', uploadedFiles)
        },
    }
}
</script>


// WEBPACK FOOTER //
// src/components/uploader/FileUpload.vue